import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';

const hasLogTeams = (sections) =>
  (Array.isArray(sections) ? sections : []).some((section) =>
    (section?.sectionTotals?.teams || []).some((team) => team && !team.dnq)
  );

const PortalLogExport = ({ event, sections, scopeLabel, onExported }) => {
  const [open, setOpen] = useState(false);
  const [pageMode, setPageMode] = useState('canvas');
  const [exporting, setExporting] = useState('');
  const [error, setError] = useState('');

  const canExport = hasLogTeams(sections) && Boolean(event);

  const handleExport = async (format) => {
    if (!canExport || exporting) return;

    setExporting(format);
    setError('');

    try {
      const { exportLogsXlsx, exportLogsPdf } = await import('../../helpers/logsExport');

      if (format === 'xlsx') {
        exportLogsXlsx(event, sections);
      } else {
        exportLogsPdf(event, sections, { pageMode });
      }
      setOpen(false);

      const teamCount = (Array.isArray(sections) ? sections : []).reduce(
        (total, section) =>
          total +
          (Array.isArray(section?.sectionTotals?.teams)
            ? section.sectionTotals.teams.filter((team) => team && !team.dnq).length
            : 0),
        0
      );
      onExported?.({ format, pageMode, teamCount });
    } catch {
      setError('The log could not be exported. Please try again.');
    } finally {
      setExporting('');
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        startIcon={<DownloadIcon />}
        onClick={() => setOpen(true)}
        disabled={!canExport}
        sx={{
          display: { xs: 'none', sm: 'inline-flex' },
          whiteSpace: 'nowrap'
        }}>
        Export log
      </Button>
      <Tooltip title="Export log">
        <span>
          <IconButton
            size="small"
            aria-label="Export log"
            onClick={() => setOpen(true)}
            disabled={!canExport}
            sx={{ display: { xs: 'inline-flex', sm: 'none' } }}>
            <DownloadIcon />
          </IconButton>
        </span>
      </Tooltip>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="xs"
        fullWidth
        aria-labelledby="portal-log-export-title">
        <DialogTitle id="portal-log-export-title">Export Log</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {scopeLabel
              ? `The ${scopeLabel.toLowerCase()} log is downloaded with its team points.`
              : 'The log is downloaded with its team points.'}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {error}
            </Alert>
          )}

          <Stack spacing={1.5} mt={2}>
            <Button
              fullWidth
              variant="outlined"
              disabled={!canExport || Boolean(exporting)}
              onClick={() => handleExport('xlsx')}
              startIcon={
                exporting === 'xlsx' ? <CircularProgress size={18} /> : undefined
              }>
              Excel (.xlsx)
            </Button>

            <Box>
              <Typography variant="subtitle2">PDF page size</Typography>
              <ToggleButtonGroup
                exclusive
                fullWidth
                size="small"
                value={pageMode}
                onChange={(event, value) => value && setPageMode(value)}
                aria-label="PDF page size"
                sx={{ mt: 0.5 }}>
                <ToggleButton value="canvas">Canvas</ToggleButton>
                <ToggleButton value="a4">A4</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <Button
              fullWidth
              variant="outlined"
              disabled={!canExport || Boolean(exporting)}
              onClick={() => handleExport('pdf')}
              startIcon={
                exporting === 'pdf' ? <CircularProgress size={18} /> : undefined
              }>
              PDF (.pdf)
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} color="primary">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PortalLogExport;
